/**
 * Fetal / intrapartum CTG engine.
 *
 * Pure, deterministic functions of absolute time `t` (seconds) so the fetal
 * heart-rate (FHR) trace and the tocodynamometer (uterine activity) trace stay
 * time-correlated — decelerations are computed from the SAME contraction model
 * that draws the toco channel, which is what makes early vs late vs variable
 * decels recognisable.
 *
 * Consumed by FetalStrip (canvas) which maps screen-x → time and samples these.
 *
 * Re-exports the pattern presets so callers have one import.
 */
export { FETAL_PATTERNS, FETAL_PATTERN_ORDER, VARIABILITY_BANDS, SCENARIO_FETAL, getScenarioFetal } from '../data/fetalPatterns';
import { VARIABILITY_BANDS } from '../data/fetalPatterns';

// ── Display scales (standard CTG paper) ──────────────────────────────────────
export const FHR_MIN = 30;
export const FHR_MAX = 240;      // FHR grid 30–240 bpm
export const TOCO_MIN = 0;
export const TOCO_MAX = 100;     // uterine activity 0–100
// Seconds of tracing visible. Kept short enough that the real-time scroll is
// clearly (if slowly) moving on screen — a full 6-min window scrolls at only
// ~1.6 px/s and reads as static. 150 s ≈ 4 px/s: visibly creeping, and still
// wide enough to show a contraction and its associated deceleration.
export const WINDOW_SEC = 150;

// Deterministic smooth "noise" from summed sines → beat-to-beat FHR variability.
function noise(t, seed = 0) {
  const s =
    Math.sin(t * 0.70 + seed) +
    Math.sin(t * 1.70 + seed * 2.3) * 0.6 +
    Math.sin(t * 3.10 + seed * 5.1) * 0.35 +
    Math.sin(t * 5.30 + seed * 1.1) * 0.2;
  return s / 2.15; // ≈ [-1, 1]
}

/**
 * Contraction state at time t. intensity ∈ [0,1] is the normalised rise of the
 * current contraction (0 at rest, 1 at peak); used both to draw toco and to
 * couple decelerations to contractions.
 */
export function contractionPhase(t, fetal) {
  const freq = fetal.contractionFreq ?? 0;
  if (freq <= 0) return { intensity: 0, period: Infinity, dur: 0, phase: 0 };
  const period = 600 / freq;                 // seconds between contraction onsets
  const phase = ((t % period) + period) % period;
  const dur = Math.min(90, period * 0.6);    // contraction lasts up to ~90 s
  let intensity = 0;
  if (phase < dur) {
    const x = phase / dur;                    // 0..1 across the contraction
    intensity = 0.5 * (1 - Math.cos(2 * Math.PI * x)); // raised-cosine bell 0→1→0
    intensity = Math.pow(intensity, 0.85);
  }
  return { intensity, period, dur, phase };
}

/** Tocodynamometer value (0–100) at time t. */
export function tocoAt(t, fetal) {
  const tone = fetal.uterineTone ?? 10;
  const { intensity } = contractionPhase(t, fetal);
  return Math.max(0, Math.min(100, tone + (fetal.contractionStrength ?? 0) * intensity));
}

// Deceleration depth (bpm to subtract) for the active decel type.
function decelDrop(t, fetal) {
  const type = fetal.decel ?? 'none';
  if (type === 'none') return 0;
  const c = contractionPhase(t, fetal);

  if (type === 'early') {
    // Mirror image of the contraction — nadir coincides with the peak.
    return 24 * c.intensity;
  }
  if (type === 'late') {
    // Onset after the contraction starts; nadir AFTER the peak — shift the
    // contraction intensity later in time by a fixed lag.
    const lag = 20;
    const shifted = contractionPhase(t - lag, fetal).intensity;
    return 22 * shifted;
  }
  if (type === 'variable') {
    // Abrupt, sharp V/W drops near each contraction; depth jitters per event.
    const sharp = Math.pow(c.intensity, 3);           // narrow, abrupt
    const jitter = 0.75 + 0.25 * (noise(Math.floor(t / (c.period || 200)) * 13.7, 3) + 1) / 2;
    return 40 * sharp * jitter;
  }
  if (type === 'prolonged') {
    // A single sustained drop lasting minutes, recurring slowly.
    const P = 300;                                     // recur every 5 min
    const ph = ((t % P) + P) % P;
    const D = 180;                                     // 3-min deceleration
    if (ph >= D) return 0;
    const x = ph / D;
    return 46 * (0.5 * (1 - Math.cos(2 * Math.PI * x)));
  }
  return 0;
}

/** Fetal heart rate (bpm) at time t. */
export function fhrAt(t, fetal) {
  const baseline = fetal.fhrBaseline ?? 140;

  // Sinusoidal pattern overrides variability with a smooth ~3.5 cyc/min sine.
  if (fetal.sinusoidal) {
    return clampFHR(baseline + 10 * Math.sin(2 * Math.PI * t * (3.5 / 60)));
  }

  let fhr = baseline;

  // Baseline variability band (± bpm by category).
  const band = VARIABILITY_BANDS[fetal.variability] ?? 8;
  fhr += band * noise(t, 1);

  // Accelerations: smooth +18 bpm bumps (~22 s) every ~95 s.
  if (fetal.accels) {
    const P = 95;
    const ph = ((t % P) + P) % P;
    const dur = 22;
    if (ph < dur) fhr += 18 * Math.sin(Math.PI * (ph / dur));
  }

  // Decelerations coupled to the contraction model.
  fhr -= decelDrop(t, fetal);

  return clampFHR(fhr);
}

function clampFHR(v) {
  return Math.max(FHR_MIN + 10, Math.min(FHR_MAX, v));
}

/** Map a data value to a canvas y (top=high value) within [0, height]. */
export function valueToY(value, min, max, height, pad = 0) {
  const usable = height - pad * 2;
  const frac = (value - min) / (max - min);
  return pad + (1 - Math.max(0, Math.min(1, frac))) * usable;
}
